import { useState, useEffect } from 'react';
import { ArrowRight, Tag, Award } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useAffiliateClick } from '@/hooks/useAffiliateClick';
import { filterEligibleFirms } from '@/utils/featuredEligibility';

type PropFirmRow = Database['public']['Tables']['prop_firms']['Row'];

export const BestDealBanner = () => {
  const [firm, setFirm] = useState<PropFirmRow | null>(null);
  const [loading, setLoading] = useState(true);
  const { trackClick } = useAffiliateClick();

  useEffect(() => {
    const fetchFeaturedFirm = async () => {
      try {
        const { data: firmData, error: firmError } = await supabase
          .from('prop_firms')
          .select('*')
          .eq('is_active', true)
          .eq('is_featured', true)
          .order('display_order', { ascending: true });

        if (firmError) throw firmError;

        const firmIds = (firmData || []).map(f => f.id);

        const { data: programData, error: programError } = await supabase
          .from('funding_programs')
          .select('*')
          .in('prop_firm_id', firmIds)
          .eq('is_active', true);

        if (programError) throw programError;

        // Apply strict eligibility rules before selecting the top deal
        const eligible = filterEligibleFirms(firmData || [], programData || []);
        setFirm(eligible[0] ?? null);
      } catch (error) {
        console.error('Error fetching featured firm:', error);
        setFirm(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedFirm();
  }, []);

  const handleGetDealClick = () => {
    if (!firm) return;
    const targetUrl = firm.affiliate_url || firm.website_url || '#';
    trackClick({
      firmName: firm.name,
      accountSize: 'all',
      discountCode: firm.coupon_code || null,
      affiliateUrl: targetUrl,
      source: 'best_deal_banner',
      skipNavigation: true,
    });
  };

  if (loading) {
    return (
      <div className="best-deal-banner rounded-lg p-6 animate-pulse">
        <div className="h-6 bg-white/30 rounded w-48 mb-2"></div>
        <div className="h-4 bg-white/30 rounded w-64 mb-4"></div>
        <div className="h-10 bg-white/30 rounded w-36"></div>
      </div>
    );
  }

  if (!firm) return null;

  const targetUrl = firm.affiliate_url || firm.website_url || '#';
  const discountMessage = firm.discount_description
    ? firm.discount_description
    : firm.coupon_code
    ? `Use code ${firm.coupon_code} for a discount`
    : 'Special deal — limited time';

  return (
    <div className="best-deal-banner rounded-lg p-6">
      <div className="flex flex-col md:flex-row justify-between items-center">
        <div className="mb-4 md:mb-0">
          <div className="flex items-center mb-1">
            <Award className="h-5 w-5 mr-2 animate-bounce" />
            <h3 className="text-xl font-bold tracking-tight">HOT DEAL: {firm.name}</h3>
          </div>
          <p className="text-white/90 mb-2 text-base">{discountMessage}</p>
          {firm.coupon_code && (
            <div className="inline-flex items-center bg-white/20 px-3 py-1 rounded text-sm">
              <Tag className="h-4 w-4 mr-2" />
              <span>Use code <strong>{firm.coupon_code}</strong> at checkout</span>
            </div>
          )}
        </div>

        <Button
          size="lg"
          className="bg-white text-brand-darkBlue hover:bg-white/90 font-bold text-base shadow-lg shadow-black/10 hover:shadow-xl hover:scale-105 transition-all duration-200 px-8"
          asChild
        >
          <a
            href={targetUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleGetDealClick}
          >
            Get This Deal
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </Button>
      </div>
    </div>
  );
};
